import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSEO } from "@/components/common/SEO";
import { Reveal } from "@/components/common/Reveal";
import { SectionLabel } from "@/pages/Home";
import api from "@/lib/api";

export function Blog() {
  const { lang, t, ml } = useI18n();
  const L = (p) => `/${lang}${p}`;
  const [posts, setPosts] = useState([]);
  useSEO({ lang, title: `${t("nav.blog")} | HO-SYS`, description: t("blog.subtitle"), path: "/blog" });

  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)).catch(() => {}); }, []);

  return (
    <div className="pt-32">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <Reveal>
          <SectionLabel>{t("nav.blog")}</SectionLabel>
          <h1 className="mt-5 font-display font-extrabold tracking-tighter text-white text-4xl sm:text-6xl leading-[1.02] max-w-3xl">{t("blog.title")}</h1>
          <p className="mt-6 text-white/60 text-lg md:text-xl max-w-2xl">{t("blog.subtitle")}</p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        {posts.length === 0 ? (
          <p className="text-white/40">{t("blog.empty")}</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {posts.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.06}>
                <Link to={L(`/blog/${p.id}`)} data-testid={`blog-card-${p.id}`}>
                  <motion.div whileHover={{ y: -5 }} className="group h-full rounded-2xl overflow-hidden border border-white/10 bg-[#0A0A0A] hover:border-neon/40 transition-colors">
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img src={p.cover} alt={ml(p.title)} loading="lazy" className="w-full h-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105" />
                      <span className="absolute top-4 left-4 rounded-full bg-black/70 backdrop-blur px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-neon border border-neon/30">{p.tag}</span>
                    </div>
                    <div className="p-6">
                      <h3 className="font-display text-xl font-semibold text-white leading-snug">{ml(p.title)}</h3>
                      <p className="mt-3 text-white/50 text-sm leading-relaxed line-clamp-3">{ml(p.excerpt)}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-neon">
                        {t("common.readMore")} <ArrowUpRight className="w-4 h-4" />
                      </span>
                    </div>
                  </motion.div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function BlogDetail() {
  const { lang, t, ml } = useI18n();
  const { slug } = useParams();
  const L = (p) => `/${lang}${p}`;
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(null)).finally(() => setLoading(false));
  }, [slug]);

  useSEO({
    lang, title: post ? `${ml(post.title)} | HO-SYS` : "HO-SYS",
    description: post ? ml(post.excerpt) : "", path: `/blog/${slug}`, image: post?.cover,
    jsonLd: post ? { "@context": "https://schema.org", "@type": "Article", headline: ml(post.title), image: post.cover, author: { "@type": "Organization", name: "HO-SYS" } } : null,
  });

  if (loading) return <div className="pt-40 text-center text-white/50">{t("common.loading")}</div>;
  if (!post) return <div className="pt-40 text-center text-white/50">404</div>;

  return (
    <article className="pt-32">
      <section className="max-w-3xl mx-auto px-5 sm:px-8">
        <Link to={L("/blog")} data-testid="blog-back" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> {t("nav.blog")}
        </Link>
        <span className="rounded-full bg-neon/10 border border-neon/30 px-3 py-1 font-mono text-xs uppercase tracking-wider text-neon">{post.tag}</span>
        <h1 className="mt-5 font-display font-extrabold tracking-tighter text-white text-4xl sm:text-5xl leading-[1.05]">{ml(post.title)}</h1>
        <p className="mt-5 text-white/60 text-xl">{ml(post.excerpt)}</p>
      </section>
      <section className="max-w-4xl mx-auto px-5 sm:px-8 mt-10">
        <div className="rounded-3xl overflow-hidden border border-white/10">
          <img src={post.cover} alt={ml(post.title)} className="w-full h-[300px] md:h-[440px] object-cover" />
        </div>
      </section>
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
        <div className="prose prose-invert max-w-none text-white/75 text-lg leading-relaxed space-y-6">
          {ml(post.body).split("\n").filter(Boolean).map((para, i) => <p key={i}>{para}</p>)}
        </div>
      </section>
    </article>
  );
}
